import { useGate, useUnit } from 'effector-react';
import { Autocomplete, type AutocompleteProps, TextField } from '@mui/material';
import {
  ControlWrapper,
  getDefaultInputPropsAutocomplete,
  getAutocompleteOnChange,
  getAutocompleteValue,
  withControl,
} from '@unicorn/control';
import { EmployeesControlGate, employeesQuery } from './model';
import type { Employee } from './types';

export const EmployeesControl = withControl<
  Partial<AutocompleteProps<Employee, boolean, boolean, boolean>>
>(
  ({
    label,
    value,
    setValue,
    error,
    isError,
    required,
    readOnly,
    disabled,
    helperText,
    multiple,
    disableCloseOnSelect,
    clearable,
    placeholder,
    ...props
  }) => {
    useGate(EmployeesControlGate);
    const { data, pending } = useUnit(employeesQuery);

    const options = Array.isArray(data) ? data : [];

    const handleChange = (
      _: unknown,
      newValue: string | Employee | (string | Employee)[] | null
    ) => getAutocompleteOnChange(newValue, setValue, multiple);

    return (
      <ControlWrapper
        label={label}
        disabled={disabled}
        readOnly={readOnly}
        required={required}
        isError={isError}
        error={error}
        helperText={helperText}
      >
        <Autocomplete
          value={getAutocompleteValue(options, value, multiple)}
          onChange={handleChange}
          options={options}
          multiple={multiple}
          disableCloseOnSelect={
            multiple ? (disableCloseOnSelect ?? true) : disableCloseOnSelect
          }
          loading={pending}
          disableClearable={!clearable}
          getOptionKey={(option) => (option as Employee).id}
          renderInput={(params) => {
            const slotProps = {
              input: getDefaultInputPropsAutocomplete({
                params,
                readOnly,
                disabled,
                pending,
              }),
            };

            return (
              <TextField
                {...params}
                error={isError}
                placeholder={
                  multiple && Array.isArray(value) && value.length > 0 ? '' : placeholder
                }
                slotProps={slotProps}
              />
            );
          }}
          readOnly={readOnly}
          disabled={disabled}
          {...props}
        />
      </ControlWrapper>
    );
  }
);
